/**
 * Niveau Ranges Tab
 *
 * Epic 10: Manage punten ranges per niveau for automatic niveau calculation.
 * Niveaus 1-4 use punten ranges, Niveaus 5-6 use marktcorrectie percentages.
 */

import { useState } from 'react';
import { useNiveaus, useUpdateNiveau } from '../useSettings';
import type { NiveauWithStats, UpdateNiveauInput } from '../../../lib/niveausApi';

interface EditingState {
  niveauId: number;
  minPunten: string;
  maxPunten: string;
  usesMarktcorrectie: boolean;
  marktcorrectiePercentage: string;
}

export function NiveauRangesTab() {
  const { data: niveaus, isLoading, error } = useNiveaus();
  const updateNiveau = useUpdateNiveau();

  const [editing, setEditing] = useState<EditingState | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleEdit = (niveau: NiveauWithStats) => {
    setEditing({
      niveauId: niveau.id,
      minPunten: niveau.minPunten !== null ? String(niveau.minPunten) : '',
      maxPunten: niveau.maxPunten !== null ? String(niveau.maxPunten) : '',
      usesMarktcorrectie: niveau.usesMarktcorrectie,
      marktcorrectiePercentage: String(niveau.marktcorrectiePercentage),
    });
    setValidationError(null);
  };

  const handleSave = async () => {
    if (!editing) return;

    // Validation
    if (!editing.usesMarktcorrectie) {
      const min = Number(editing.minPunten);
      const max = Number(editing.maxPunten);

      if (isNaN(min) || isNaN(max)) {
        setValidationError('Voer geldige getallen in voor min en max punten');
        return;
      }

      if (min < 0 || max < 0) {
        setValidationError('Punten kunnen niet negatief zijn');
        return;
      }

      if (min > max) {
        setValidationError('Minimum punten kan niet hoger zijn dan maximum');
        return;
      }

      // Check for overlap with other niveaus
      const otherNiveaus = niveaus?.filter(n => n.id !== editing.niveauId && !n.usesMarktcorrectie) || [];

      for (const other of otherNiveaus) {
        if (other.minPunten !== null && other.maxPunten !== null) {
          // Check overlap: ranges overlap if one starts before the other ends AND ends after the other starts
          if (min <= other.maxPunten && max >= other.minPunten) {
            setValidationError(`Range overlapt met ${other.name} (${other.minPunten}-${other.maxPunten} punten)`);
            return;
          }
        }
      }
    }

    const data: UpdateNiveauInput = editing.usesMarktcorrectie
      ? {
          minPunten: null,
          maxPunten: null,
          usesMarktcorrectie: true,
          marktcorrectiePercentage: Number(editing.marktcorrectiePercentage),
        }
      : {
          minPunten: Number(editing.minPunten),
          maxPunten: Number(editing.maxPunten),
          usesMarktcorrectie: false,
          marktcorrectiePercentage: 0,
        };

    try {
      await updateNiveau.mutateAsync({ id: editing.niveauId, data });
      setEditing(null);
      setValidationError(null);
    } catch (err) {
      setValidationError('Fout bij opslaan van niveau configuratie');
    }
  };

  const handleCancel = () => {
    setEditing(null);
    setValidationError(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Laden...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-sm text-red-700">Fout bij het laden van niveaus.</p>
      </div>
    );
  }

  // Separate niveaus by type
  const puntenNiveaus = niveaus?.filter(n => !n.usesMarktcorrectie) || [];
  const marktcorrectieNiveaus = niveaus?.filter(n => n.usesMarktcorrectie) || [];

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-lg font-medium text-gray-900">Niveau Punten-ranges</h2>
        <p className="text-sm text-gray-500">
          Configureer de punten-ranges voor automatische niveau-bepaling op basis van takenwegingen
        </p>
      </div>

      {/* Info Banner */}
      <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <svg className="h-5 w-5 text-blue-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="text-sm text-blue-700">
            <p className="font-medium mb-1">Hoe werkt niveau-bepaling?</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Het niveau van een medewerker wordt bepaald door de som van de wegingen van toegewezen taken</li>
              <li>Niveaus 1-4 zijn gebaseerd op punten-ranges (bijv. 10-15 punten = Niveau 1)</li>
              <li>Niveaus 5-6 gebruiken marktcorrectie (handmatige toewijzing met salarisbonus)</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Validation Error */}
      {validationError && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-red-700">{validationError}</p>
          </div>
        </div>
      )}

      {/* Punten-based Niveaus */}
      <div className="mb-8">
        <h3 className="text-md font-medium text-gray-900 mb-4">Punten-gebaseerde Niveaus</h3>
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Niveau
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Min. Punten
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Max. Punten
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Medewerkers
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Bereik
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[160px]">
                  Acties
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {puntenNiveaus.map((niveau) => (
                <tr key={niveau.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-800 font-semibold mr-3">
                        {niveau.id}
                      </span>
                      <div>
                        <div className="text-sm font-medium text-gray-900">{niveau.name}</div>
                        {niveau.beschrijving && (
                          <div className="text-xs text-gray-500">{niveau.beschrijving}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editing?.niveauId === niveau.id && !editing.usesMarktcorrectie ? (
                      <input
                        type="number"
                        value={editing.minPunten}
                        onChange={(e) => setEditing({ ...editing, minPunten: e.target.value })}
                        className="w-20 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        min={0}
                      />
                    ) : (
                      <span className="text-sm font-medium text-gray-900">
                        {niveau.minPunten !== null ? niveau.minPunten : '-'}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editing?.niveauId === niveau.id && !editing.usesMarktcorrectie ? (
                      <input
                        type="number"
                        value={editing.maxPunten}
                        onChange={(e) => setEditing({ ...editing, maxPunten: e.target.value })}
                        className="w-20 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                        min={0}
                      />
                    ) : (
                      <span className="text-sm font-medium text-gray-900">
                        {niveau.maxPunten !== null ? niveau.maxPunten : '-'}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {niveau.employeesCount} medewerkers
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {niveau.minPunten !== null && niveau.maxPunten !== null ? (
                      <div className="flex items-center">
                        <div className="flex-1 bg-gray-200 rounded-full h-2 w-20 mr-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{
                              width: `${Math.min(100, ((niveau.maxPunten - niveau.minPunten + 1) / 56) * 100)}%`,
                            }}
                          />
                        </div>
                        <span className="text-xs text-gray-500">
                          {niveau.maxPunten - niveau.minPunten + 1} punten
                        </span>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">Niet geconfigureerd</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium min-w-[160px]">
                    {editing?.niveauId === niveau.id ? (
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={handleCancel}
                          className="text-gray-600 hover:text-gray-900"
                        >
                          Annuleren
                        </button>
                        <button
                          onClick={handleSave}
                          disabled={updateNiveau.isPending}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          {updateNiveau.isPending ? 'Opslaan...' : 'Opslaan'}
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleEdit(niveau)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Bewerken
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Marktcorrectie Niveaus */}
      <div>
        <h3 className="text-md font-medium text-gray-900 mb-4">Marktcorrectie Niveaus</h3>
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex">
            <svg className="h-5 w-5 text-amber-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <p className="text-sm text-amber-700">
              Marktcorrectie niveaus worden handmatig toegewezen en verhogen het basissalaris met het geconfigureerde percentage.
            </p>
          </div>
        </div>
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Niveau
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Marktcorrectie %
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Medewerkers
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Effect
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[160px]">
                  Acties
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {marktcorrectieNiveaus.map((niveau) => (
                <tr key={niveau.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-amber-100 text-amber-800 font-semibold mr-3">
                        {niveau.id}
                      </span>
                      <div>
                        <div className="text-sm font-medium text-gray-900">{niveau.name}</div>
                        {niveau.beschrijving && (
                          <div className="text-xs text-gray-500">{niveau.beschrijving}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editing?.niveauId === niveau.id && editing.usesMarktcorrectie ? (
                      <div className="flex items-center">
                        <span className="text-gray-500 mr-1">+</span>
                        <input
                          type="number"
                          value={editing.marktcorrectiePercentage}
                          onChange={(e) => setEditing({ ...editing, marktcorrectiePercentage: e.target.value })}
                          className="w-16 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                          min={0}
                          max={100}
                        />
                        <span className="text-gray-500 ml-1">%</span>
                      </div>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-green-100 text-green-800">
                        +{niveau.marktcorrectiePercentage}%
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {niveau.employeesCount} medewerkers
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-600">
                      Basissalaris +{niveau.marktcorrectiePercentage}%
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium min-w-[160px]">
                    {editing?.niveauId === niveau.id ? (
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={handleCancel}
                          className="text-gray-600 hover:text-gray-900"
                        >
                          Annuleren
                        </button>
                        <button
                          onClick={handleSave}
                          disabled={updateNiveau.isPending}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          {updateNiveau.isPending ? 'Opslaan...' : 'Opslaan'}
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleEdit(niveau)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Bewerken
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary Stats */}
      {niveaus && niveaus.length > 0 && (
        <div className="mt-8 bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Overzicht</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-2xl font-semibold text-gray-900">
                {niveaus.reduce((sum, n) => sum + n.employeesCount, 0)}
              </p>
              <p className="text-sm text-gray-500">Totaal medewerkers</p>
            </div>
            <div>
              <p className="text-2xl font-semibold text-blue-600">
                {puntenNiveaus.length}
              </p>
              <p className="text-sm text-gray-500">Punten-niveaus</p>
            </div>
            <div>
              <p className="text-2xl font-semibold text-amber-600">
                {marktcorrectieNiveaus.length}
              </p>
              <p className="text-sm text-gray-500">Marktcorrectie niveaus</p>
            </div>
            <div>
              <p className="text-2xl font-semibold text-gray-900">
                {puntenNiveaus.reduce((max, n) => Math.max(max, n.maxPunten || 0), 0)}
              </p>
              <p className="text-sm text-gray-500">Max. punten bereik</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
